import { Link } from 'react-router-dom';
import type { NewsItem } from '@/types/content';
import { formatDate } from '@/utils/date';
import FavoriteButton from './FavoriteButton';

export const NEWS_TYPE_LABELS: Record<string, string> = {
  news: '资讯',
  announcement: '公告',
  event: '活动',
};

const TYPE_STYLES: Record<string, string> = {
  news: 'bg-primary-100 text-primary-800',
  announcement: 'bg-secondary-100 text-secondary-900',
  event: 'bg-accent-100 text-accent-800',
};

interface NewsCardProps {
  item: NewsItem;
  variant?: 'default' | 'wide';
}

export default function NewsCard({ item, variant = 'default' }: NewsCardProps) {
  if (variant === 'wide') {
    return (
      <article className="gs-card-hover group flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-3 sm:flex-row">
        <Link to={`/news/${item.id}`} className="block w-full shrink-0 cursor-pointer overflow-hidden rounded-lg sm:w-56">
          <div className="h-40 w-full sm:h-36">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} 原神资讯`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span className={`rounded-full px-2 py-0.5 font-label ${TYPE_STYLES[item.type] ?? 'bg-background-100 text-foreground-700'}`}>
                {NEWS_TYPE_LABELS[item.type] ?? '资讯'}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {formatDate(item.date)}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-time-line"></i>
                {item.readTime}
              </span>
            </div>
            <h3 className="mt-2.5 line-clamp-2 font-heading text-base font-bold text-foreground-950">
              <Link to={`/news/${item.id}`} className="cursor-pointer transition-colors hover:text-primary-700">
                {item.title}
              </Link>
            </h3>
            <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{item.excerpt}</p>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-foreground-400">{item.author}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-400">{item.category}</span>
              <FavoriteButton id={`news-${item.id}`} />
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="gs-card-hover group flex h-full flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50">
      <Link to={`/news/${item.id}`} className="block cursor-pointer">
        <div className="relative h-44 w-full overflow-hidden">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} 原神资讯`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-label ${TYPE_STYLES[item.type] ?? 'bg-background-100 text-foreground-700'}`}>
            {NEWS_TYPE_LABELS[item.type] ?? '资讯'}
          </span>
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {formatDate(item.date)}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line"></i>
            {item.readTime}
          </span>
        </div>
        <h3 className="mt-2.5 line-clamp-2 font-heading text-base font-bold text-foreground-950">
          <Link to={`/news/${item.id}`} className="cursor-pointer transition-colors hover:text-primary-700">
            {item.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-foreground-600">{item.excerpt}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3">
          <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
            <i className="ri-price-tag-3-line"></i>
            {item.tags[0]}
          </span>
          <FavoriteButton id={`news-${item.id}`} />
        </div>
      </div>
    </article>
  );
}